import React, { useState } from 'react';
import { X, Check, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import {
  CAPABILITY_MATURITY_DESCRIPTIONS,
  CAPABILITY_VENDOR_MAPPINGS,
  MATURITY_LEVELS,
} from '../config/ztaArchitectureData';

// ============================================================================
// VISIBILITY TOOL MODAL
// ============================================================================

const VisibilityToolModal = ({ tool, data, suggestion, onClose, onSave, saving, departmentName, departmentId }) => {
  // Get vendor/product mappings for this tool (same as capabilities)
  const vendorMapping = CAPABILITY_VENDOR_MAPPINGS[tool.id] || { vendors: [], products: {} };

  // Parse initial vendors
  const getInitialSelectedVendors = () => {
    if (data.vendors?.length) {
      return data.vendors.map(v => {
        const match = v.match(/^([^(]+)(?:\s*\(([^)]+)\))?$/);
        if (match) {
          return { vendor: match[1].trim(), product: match[2]?.trim() || null };
        }
        return { vendor: v.trim(), product: null };
      });
    }
    if (suggestion?.topVendor && !data.vendors?.length) {
      return [{ vendor: suggestion.topVendor, product: suggestion.topProduct || null }];
    }
    return [];
  };

  const [selectedVendors, setSelectedVendors] = useState(getInitialSelectedVendors());
  const [currentVendor, setCurrentVendor] = useState('');
  const [currentProduct, setCurrentProduct] = useState('');
  const [maturity, setMaturity] = useState(data.maturity || 'TRAD.');
  const [notes, setNotes] = useState(data.notes || '');

  // Get available products for selected vendor
  const availableProducts = currentVendor ? (vendorMapping.products[currentVendor] || []) : [];

  // Add vendor/product to selection
  const handleAddVendor = (vendor = currentVendor, product = currentProduct) => {
    if (!vendor) return;
    const newEntry = { vendor, product: product || null };
    const exists = selectedVendors.some(v => v.vendor === newEntry.vendor && v.product === newEntry.product);
    if (!exists) {
      setSelectedVendors(prev => [...prev, newEntry]);
    }
    setCurrentVendor('');
    setCurrentProduct('');
  };

  // Auto-add when product is selected
  const handleProductSelect = (product) => {
    setCurrentProduct(product);
    if (product && currentVendor) {
      handleAddVendor(currentVendor, product);
    }
  };

  // Remove vendor from selection
  const handleRemoveVendor = (index) => {
    setSelectedVendors(selectedVendors.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    let finalVendors = [...selectedVendors];
    if (currentVendor) {
      const pendingEntry = { vendor: currentVendor, product: currentProduct || null };
      const exists = finalVendors.some(v => v.vendor === pendingEntry.vendor && v.product === pendingEntry.product);
      if (!exists) {
        finalVendors.push(pendingEntry);
      }
    }
    const vendorStrings = finalVendors.map(v => v.product ? `${v.vendor} (${v.product})` : v.vendor);
    onSave({ vendors: vendorStrings, maturity, notes });
  };

  const maturityDescriptions = CAPABILITY_MATURITY_DESCRIPTIONS[tool.id] || {};

  return (
    <div className="zta-modal-backdrop" onClick={onClose}>
      <div className="zta-modal zta-modal--capability" onClick={e => e.stopPropagation()}>
        <div className="zta-modal-header">
          <div>
            <h2 className="zta-modal-title">{tool.name}</h2>
            <p className="zta-modal-subtitle">Visibility & Analytics{departmentName && ` · ${departmentName}`}</p>
          </div>
          <button className="zta-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="zta-modal-body zta-modal-body--scrollable">
          {/* Description */}
          {maturityDescriptions.description && (
            <div className="zta-capability-description">
              {maturityDescriptions.description}
            </div>
          )}

          {/* Suggestion Banner */}
          {suggestion?.topVendor && (
            <div className="zta-suggestion-banner">
              <div className="zta-suggestion-banner-content">
                <Sparkles size={16} />
                <span>
                  Based on your purchase history: <strong>{suggestion.topVendor}</strong>
                  {suggestion.topProduct && ` (${suggestion.topProduct})`}
                  {suggestion.totalSpend && ` • $${Math.round(suggestion.totalSpend).toLocaleString()} total spend`}
                </span>
              </div>
            </div>
          )}

          {/* Vendor Selection */}
          <div className="zta-form-group">
            <label className="zta-form-label">Vendor/Product Selection</label>
            <div className="zta-vendor-selector">
              <select
                className="zta-form-select"
                value={currentVendor}
                onChange={(e) => {
                  setCurrentVendor(e.target.value);
                  setCurrentProduct('');
                }}
              >
                <option value="">Select vendor...</option>
                {vendorMapping.vendors.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              {availableProducts.length > 0 && (
                <select
                  className="zta-form-select"
                  value={currentProduct}
                  onChange={(e) => handleProductSelect(e.target.value)}
                >
                  <option value="">Select product...</option>
                  {availableProducts.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              )}
              <button
                className="zta-modal-btn zta-modal-btn--secondary"
                onClick={() => handleAddVendor()}
                disabled={!currentVendor}
              >
                Add
              </button>
            </div>
            {selectedVendors.length > 0 && (
              <div className="zta-selected-vendors">
                {selectedVendors.map((v, i) => (
                  <span key={i} className="zta-vendor-chip">
                    {v.product ? `${v.vendor} (${v.product})` : v.vendor}
                    <button onClick={() => handleRemoveVendor(i)}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Maturity Selection */}
          <div className="zta-maturity-section">
            <h3>Current Maturity Level</h3>
            <p className="zta-maturity-instruction">Select the maturity level that best describes current implementation</p>
            <div className="zta-maturity-cards">
              {MATURITY_LEVELS.map(level => {
                const levelKey = { 'TRAD.': 'traditional', 'INITIAL': 'initial', 'ADVANCED': 'advanced', 'OPTIMAL': 'optimal' }[level];
                return (
                  <button
                    key={level}
                    className={`zta-maturity-card ${maturity === level ? 'zta-maturity-card--selected' : ''}`}
                    onClick={() => setMaturity(level)}
                  >
                    <div className="zta-maturity-card-header">
                      <span className="zta-maturity-card-level">{level}</span>
                      {maturity === level && <CheckCircle size={16} />}
                    </div>
                    {maturityDescriptions[levelKey] && (
                      <p className="zta-maturity-card-desc">{maturityDescriptions[levelKey]}</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="zta-form-group">
            <label className="zta-form-label">Implementation Notes</label>
            <textarea
              className="zta-form-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about the implementation..."
              rows={3}
            />
          </div>
        </div>

        <div className="zta-modal-footer">
          <button className="zta-modal-btn zta-modal-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="zta-modal-btn zta-modal-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisibilityToolModal;
